import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useAuth } from '../../Contexts/AuthContext';

export default function GeneralTab({ 
  isDataFullyLoaded = true,
  exportButtonEnabled = false,
  exportFileLoading = false,
  onExportData,
  onOpenGLKeyManagement,
}) {
  const auth = useAuth();
  
  const handleExport = () => {
    if (onExportData) {
      onExportData();
    }
  };

  const handleGLKeyManagement = () => {
    if (onOpenGLKeyManagement) {
      onOpenGLKeyManagement();
    }
  };

  // UserType enum values from backend: 0 = SUPER_USER, 1 = ADMIN_USER, 2 = CONTRIBUTOR, 3 = OBSERVER
  const isAdmin = auth.activeUser?.userType === 0 || auth.activeUser?.userType === 1;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        General Settings
      </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Manage project-wide settings and perform common actions.
      </Typography>

      {/* GL Key Management Section - Admin Only */}
      {isAdmin && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <AccountTreeIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                GL Key Management
              </Typography>
            </Box>
            
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Manage your chart of accounts including entities, departments, and account details. 
              Edit GL Keys, monthly values, and taxonomy structure.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AccountTreeIcon />}
                onClick={handleGLKeyManagement}
                disabled={!isDataFullyLoaded}
              >
                Manage GL Keys
              </Button>
              
              {!isDataFullyLoaded && (
                <Typography variant="caption" color="textSecondary">
                  Waiting for project data to load...
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Export Section */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Data Export
            </Typography>
          </Box>
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Export your project data including all changes and account information to a multi-sheet Excel workbook (.xlsx).
            The exported file can also be re-uploaded to create a new project with all changes included.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="contained"
              color="info"
              startIcon={exportFileLoading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
              onClick={handleExport}
              disabled={!exportButtonEnabled || exportFileLoading || !isDataFullyLoaded}
            >
              {exportFileLoading ? 'Exporting...' : 'Export Project Data'}
            </Button>
            
            {!isDataFullyLoaded && (
              <Typography variant="caption" color="textSecondary">
                Waiting for project data to load...
              </Typography>
            )}
            
            {isDataFullyLoaded && !exportButtonEnabled && (
              <Typography variant="caption" color="textSecondary">
                No data available to export
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Placeholder for future settings */}
      <Card elevation={2} sx={{ mb: 2, bgcolor: 'grey.50' }}>
        <CardContent>
          <Typography variant="body2" color="textSecondary" fontStyle="italic">
            Additional project settings will be added here in future updates.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
